import React, { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import BaseButton from '@mui/material/Button';
import styled from 'styled-components';
import { createSelector } from 'reselect';
import { get } from 'lodash';
import {
 FaChevronDown, FaChevronUp, FaFileDownload, FaLock,
} from 'react-icons/fa';
import {
  CONTRAST,
  SUCCESS,
  BACKGROUND_PRIMARY,
  QUARTER_CONTRAST,
} from '../../theme';
import { Checkbox } from '../../ui';

const LayerList = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-right: 10px;
`;
const Column = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 10px;
  margin-bottom: 6px;
  padding-bottom: 6px;
  border-bottom: 1px solid ${QUARTER_CONTRAST};
`;

const Actions = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
`;

const Button = styled(BaseButton)`
  .MuiButton-label {
    font-size: 10px;
  }
`;

const AccordianGroup = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 8px;
`;

const AccordianHeader = styled.div`
  cursor: pointer;
  font-size: 16px;
  color: ${CONTRAST};
  font-weight: 500;
  padding-bottom: 8px;
  padding-right: 12px;
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  border-bottom: ${({ isSelected }) => (isSelected ? 0 : `1px solid ${BACKGROUND_PRIMARY}`)};
`;

const Dot = styled.div`
  height: 8px;
  width: 8px;
  border-radius: 4px;
  background-color: ${SUCCESS};
  margin-right: 6px;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
`;

const mapState = createSelector(
  ({ layersSelected }) => layersSelected,
  ({ accordian }) => accordian,
  ({ accordiansOpen }) => accordiansOpen,
  ({ customLayers }) => customLayers,
  ({ reports }) => reports,
  ({ customLayersConfig }) => customLayersConfig,
  ({ timelines }) => timelines,
  (
    layersSelected,
    accordian,
    accordiansOpen,
    customLayers,
    reports,
    customLayersConfig,
    timelines,
  ) => {
    let finalAccordian = accordian;

    if (customLayersConfig && Object.values(customLayers).length) {
      const myCustomLayers = Object.values(customLayers).map((layer) => {
        // eslint-disable-next-line no-param-reassign
        layer.isCustom = true;
        return layer;
      }).filter(layer => layer.owner);

      const layersSharedWithMe = Object.values(customLayers).map((layer) => {
        // eslint-disable-next-line no-param-reassign
        layer.isCustom = true;
        return layer;
      }).filter(layer => !layer.owner);

      finalAccordian = [
        myCustomLayers.length ? {
          header: 'Custom Layers',
          layers: myCustomLayers,
        } : undefined,
        layersSharedWithMe.length ? {
          header: 'Layers Shared With Me',
          layers: layersSharedWithMe,
        } : undefined,
        ...accordian,
      ];
    }

    return ({
      reports,
      layersSelected,
      accordian: finalAccordian.filter(Boolean).flat(),
      accordiansOpen,
      multiparcelReport: reports.find(({ id }) => id === 'MULTI_PARCEL_REPORT'),
      timelinesSelected: !!Object.keys(timelines).length,
    });
  },
);

const getLayerOptionsLoading = ({ layerOptionsLoading }) => layerOptionsLoading;
export const Layers = ({ setOpenNav }) => {
  const {
    layersSelected,
    accordian,
    accordiansOpen,
    reports,
    multiparcelReport,
    timelinesSelected,
  } = useSelector(mapState);
  const layerOptionsLoading = useSelector(getLayerOptionsLoading);

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [downloadLayer, setDownloadLayer] = React.useState(null);
  const dispatch = useDispatch();

  const handleClick = (event, layer) => {
    setAnchorEl(event.currentTarget);
    setDownloadLayer(layer);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setDownloadLayer(null);
  };

  const downloadCSV = useCallback(() => {
    const props = downloadLayer.download.keys;

    const filterString = downloadLayer.filterString || '';
    dispatch({
      type: 'DOWNLOAD_CSV',
      payload: {
        filterString,
        props,
        source: downloadLayer.source,
        timeline: downloadLayer.timeline,
      },
    });

    dispatch({
      type: 'TRACK',
      payload: {
        name: 'Download CSV',
        filterString,
        props,
      },
    });
    setDownloadLayer(null);
    setAnchorEl(null);
  }, [downloadLayer, dispatch, setDownloadLayer, setAnchorEl]);

  const downloadShapefile = useCallback(() => {
    const props = downloadLayer.download.keys;

    const filterString = downloadLayer.filterString || '';
    dispatch({
      type: 'DOWNLOAD_SHAPEFILE',
      payload: {
        props,
        filterString,
        source: downloadLayer.source,
        timeline: downloadLayer.timeline,
      },
    });

    dispatch({
      type: 'TRACK',
      payload: {
        name: 'Download Shapefile',
        filterString,
        props,
      },
    });

    setDownloadLayer(null);
    setAnchorEl(null);
  }, [downloadLayer, setDownloadLayer, setAnchorEl, dispatch]);

  const selectLayer = useCallback(({ key, checked, layer }) => {
    const {
      label, source, isCustom, filters, timeline,
    } = layer;

    dispatch({
      type: 'ADD_LAYER',
      payload: {
        ...layer,
        key,
        checked,
      },
    });
    if (timeline && checked) {
      dispatch({
        type: 'SELECT_TIMELINE',
        payload: timeline,
      });
      dispatch({
        type: 'SET_BOTTOM_PANEL',
        payload: {
          page: 'timeline',
        },
      });
    } else if (timeline && !checked) {
      dispatch({
        type: 'DESELECT_TIMELINE',
        payload: timeline,
      });

      if (timelinesSelected) {
        dispatch({ type: 'SET_PLAYING', payload: false });
        dispatch({ type: 'CLEAR_DATE_RANGE' });
      }
    }

    dispatch({
      type: 'TRACK',
      payload: {
        name: 'Layer Selected',
        isCustom,
        label,
        source,
        column: key,
        filters,
      },
    });
  }, [dispatch, timelinesSelected]);
  return (
    <LayerList>
      {layerOptionsLoading ? (
        <AccordianGroup>
          <AccordianHeader>Loading</AccordianHeader>
        </AccordianGroup>
      ) : (
        accordian.map(({ header, layers }, i) => {
          const isSelected = accordiansOpen.includes(i);
          const hasActiveLayers = !!layers.find(
            ({ key }) => layersSelected[key],
          );

          return (
            <AccordianGroup key={header}>
              <AccordianHeader
                isSelected={isSelected}
                onClick={() => dispatch({
                  type: isSelected ? 'CLOSE_ACCORDIAN' : 'OPEN_ACCORDIAN',
                  payload: { index: i },
                })}
              >
                <Row>
                  <Dot visible={hasActiveLayers} />
                  {header}
                </Row>
                {isSelected ? (
                  <FaChevronUp size={15} />
                ) : (
                  <FaChevronDown size={15} />
                )}
              </AccordianHeader>
              <Menu
                id="download-menu"
                elevation={2}
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem onClick={downloadCSV}>Download CSV</MenuItem>
                {
                  get(downloadLayer, 'download.exclude', []).indexOf('shapefile') > -1
                    ? null
                    : (
                      <MenuItem onClick={downloadShapefile}>
                        Download Shapefile
                      </MenuItem>
                    )
                }
              </Menu>
              {!isSelected
                ? null
                : layers.map((layer) => {
                  const key = `${layer.key}-${layer.label}`;
                  if (!layer.isCustom) {
                    return (
                      <Layer
                        layer={layer}
                        layersSelected={layersSelected}
                        selectLayer={selectLayer}
                        key={key}
                        showDownload={handleClick}
                      />
                    );
                  }
                  return (
                    <CustomLayer
                      key={key}
                      reports={reports}
                      layer={layer}
                      layersSelected={layersSelected}
                      selectLayer={selectLayer}
                      setOpenNav={setOpenNav}
                      multiparcelReport={multiparcelReport}
                    />
                  );
                })}
            </AccordianGroup>
          );
        })
      )}
      {layerOptionsLoading ? null : (
        <Button
          onClick={() => {
            dispatch({ type: 'DESELECT_LAYERS' });
            Object.values(accordian).map(({ layers }) => layers.forEach((layer) => {
              if (layer.default) {
                dispatch({
                  type: 'ADD_LAYER',
                  payload: { key: layer.key, checked: true },
                });
              }
            }));
          }}
          style={{ marginRight: 16 }}
        >
          Clear Selected Layers
        </Button>
      )}
    </LayerList>
  );
};

const Layer = ({
  layer, layersSelected, selectLayer, showDownload,
}) => {
  const {
    label, key, download, timeline, locked,
  } = layer;
  return (
    <Row
      style={{
        opacity: locked ? 0.7 : 1,
        position: 'relative',
      }}
    >
      <Checkbox
        onClick={(k, c) => {
          if (!locked) {
            selectLayer({
              key: k,
              checked: c,
              layer,
            });
          }
        }}
        id={key}
        label={label}
        timeline={timeline}
        checked={Boolean(layersSelected[key])}
      />
      {locked ? (
        <FaLock
          style={{
            position: 'absolute',
            left: 16,
            top: 13,
            width: 10,
          }}
        />
      ) : null}
      {download ? (
        <FaFileDownload
          style={{
            padding: 8,
            cursor: 'pointer',
            width: 20,
            maxWidth: 20,
          }}
          size={12}
          onClick={(e) => showDownload(e, layer)}
        />
      ) : null}
    </Row>
  );
};

const CustomLayer = ({
  layer,
  layersSelected,
  selectLayer,
  setOpenNav,
  multiparcelReport,
}) => {
  const { label, key } = layer;
  const dispatch = useDispatch();
  const openModal = (l) => dispatch({
    type: 'OPEN_MODAL',
    payload: {
      type: 'MULTI_PARCEL',
      data: {
        config: {
          layer: l,
          report: multiparcelReport,
        },
      },
    },
  });
  return (
    <Column>
      <Checkbox
        onClick={(k, c) => selectLayer({
          key: k,
          checked: c,
          layer,
        })}
        id={key}
        label={label}
        checked={Boolean(layersSelected[key])}
      />
      <Actions>
        <Button
          size="small"
          onClick={() => {
            dispatch({
              type: 'CREATE_NEW_CUSTOM_LAYERS',
              payload: { layers: [layer] },
            });
            selectLayer({
              key,
              checked: false,
              layer,
            });
            setOpenNav('LAYER_BUILDER');
          }}
        >
          Edit
        </Button>
        <Button
          size="small"
          onClick={() => {
            // eslint-disable-next-line no-restricted-globals
            const verified = confirm(
              'Are you sure you want to delete this layer? You cannot undo this.',
            );
            if (verified) {
              dispatch({
                type: 'REMOVE_CUSTOM_LAYERS',
                payload: { key: layer.key },
              });
            }
          }}
        >
          Delete
        </Button>
        {multiparcelReport &&
          !(multiparcelReport.excludeSources || []).includes(layer.source) ? (
          <Button size="small" onClick={() => openModal(layer)}>
            Generate Report
          </Button>
        ) : null}
      </Actions>
    </Column>
  );
};
